import { useState } from 'react';
import {
  Ban,
  Check,
  CircleSlash,
  Clock,
  Eye,
  MailX,
  MousePointerClick,
  Send,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Pill } from '@/components/StatusPill';
import { ErrorState, Skeleton } from '@/components/States';
import { useRecipientTimeline } from '@/hooks/use-contacts';
import { formatDateTime } from '@/lib/utils';
import type { EmailEventType, RecipientEvent, RecipientSendSummary } from '@/lib/schemas';

/**
 * One person's delivery history — the screen that answers "I never got your email".
 *
 * ── THE THING THIS SCREEN MUST GET RIGHT ─────────────────────────────────────
 *
 * The commonest true answer is "it was never sent to you", and that leaves NO events behind — a
 * dropped message was suppressed before submission, so the event stream is empty and only the
 * per-campaign summary knows. So the summary comes first and the event stream second, which is the
 * reverse of what a timeline usually looks like. A screen that led with the events would show a blank
 * panel for the most common case it exists to explain.
 *
 * Machine opens are LABELLED rather than filtered. "Did they read it?" for an Apple Mail recipient has
 * an honest answer — something fetched the image and we cannot tell — and it is only sayable if the
 * flag is on screen.
 */
export function RecipientTimelineDialog({
  contactId,
  email,
  onClose,
}: {
  contactId: string;
  email: string;
  onClose: () => void;
}) {
  // Cursor state, not a page number: the server pages by keyset on `occurred_at`.
  const [before, setBefore] = useState<string | undefined>(undefined);

  const { data, isPending, isError, error, refetch } = useRecipientTimeline(contactId, before);

  return (
    <Modal
      title="Delivery history"
      description={email}
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-32" />
        </div>
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className="flex flex-col gap-5">
          <Sends sends={data.sends} />
          <Events
            events={data.events}
            nextBefore={data.nextBefore}
            onMore={() => setBefore(data.nextBefore ?? undefined)}
          />
        </div>
      )}
    </Modal>
  );
}

/**
 * What was sent, per campaign. First on the page on purpose — see the file comment.
 */
function Sends({ sends }: { sends: RecipientSendSummary[] }) {
  if (sends.length === 0) {
    return (
      <p className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-muted">
        This person has never been included in a campaign. If they were expecting one, they were not in
        the audience — check the segment rather than the delivery.
      </p>
    );
  }

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Campaigns</h3>

      <ul className="flex flex-col gap-2">
        {sends.map((send) => (
          <li
            key={`${send.campaignId}-${send.createdAt}`}
            className="rounded-md border border-border bg-surface px-3 py-2"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-heading">{send.campaignName}</p>
              <Outcome send={send} />
            </div>

            <p className="mt-1 text-xs text-muted">{describeSend(send)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The one-word answer, and it is deliberately the FAILURE that wins when there is one.
 *
 * A message that was delivered and then bounced on a later retry, or delivered and then complained
 * about, should not read as "Delivered" — the thing somebody needs to know is the worst thing that
 * happened, not the last.
 */
function Outcome({ send }: { send: RecipientSendSummary }) {
  if (send.droppedAt !== null) return <Pill tone="neutral">Not sent</Pill>;
  if (send.bouncedAt !== null) {
    return <Pill tone="danger">{send.bounceType === 'blocked' ? 'Blocked' : 'Bounced'}</Pill>;
  }
  if (send.complainedAt !== null) return <Pill tone="danger">Marked as spam</Pill>;
  if (send.unsubscribedAt !== null) return <Pill tone="warning">Unsubscribed</Pill>;
  if (send.deliveredAt !== null) return <Pill tone="success">Delivered</Pill>;

  return <Pill tone="neutral">Submitted</Pill>;
}

/** Plain-language detail under the pill. The reasons come from the provider, so they are shown verbatim. */
function describeSend(send: RecipientSendSummary): string {
  if (send.droppedAt !== null) {
    return `Suppressed before it was sent${send.droppedReason === null ? '' : ` — ${send.droppedReason}`}. ${formatDateTime(send.droppedAt)}`;
  }

  const parts: string[] = [];

  if (send.deliveredAt !== null) parts.push(`Delivered ${formatDateTime(send.deliveredAt)}`);

  if (send.bouncedAt !== null) {
    parts.push(
      `Bounced ${formatDateTime(send.bouncedAt)}${send.bounceReason === null ? '' : ` — ${send.bounceReason}`}`,
    );
  }

  if (send.firstOpenedAt !== null) {
    // The human count is what gets quoted; the raw count is shown beside it only when they differ, so
    // nobody has to wonder which number they are looking at.
    const machine = send.openCount - send.humanOpenCount;

    parts.push(
      send.humanOpenCount > 0
        ? `Opened ${formatDateTime(send.firstOpenedAt)}`
        : `Image fetched ${formatDateTime(send.firstOpenedAt)}, but every open looks automated`,
    );

    if (machine > 0 && send.humanOpenCount > 0) {
      parts.push(`${machine} further open${machine === 1 ? '' : 's'} looked automated`);
    }
  }

  if (send.firstClickedAt !== null) parts.push(`Clicked ${formatDateTime(send.firstClickedAt)}`);
  if (send.unsubscribedAt !== null) parts.push(`Unsubscribed ${formatDateTime(send.unsubscribedAt)}`);

  return parts.length === 0
    ? `Handed to the provider ${formatDateTime(send.createdAt)}, nothing back yet`
    : parts.join(' · ');
}

const eventStyles: Record<EmailEventType, { icon: LucideIcon; label: string; tone: string }> = {
  Processed: { icon: Send, label: 'Accepted by the provider', tone: 'text-muted' },
  Dropped: { icon: CircleSlash, label: 'Not sent', tone: 'text-muted' },
  Delivered: { icon: Check, label: 'Delivered', tone: 'text-success-ink' },
  Deferred: { icon: Clock, label: 'Deferred, will retry', tone: 'text-warning-ink' },
  Bounce: { icon: Ban, label: 'Bounced', tone: 'text-danger-ink' },
  Open: { icon: Eye, label: 'Opened', tone: 'text-foreground' },
  Click: { icon: MousePointerClick, label: 'Clicked a link', tone: 'text-foreground' },
  SpamReport: { icon: ShieldAlert, label: 'Marked as spam', tone: 'text-danger-ink' },
  Unsubscribe: { icon: MailX, label: 'Unsubscribed', tone: 'text-warning-ink' },
  GroupUnsubscribe: { icon: MailX, label: 'Unsubscribed from a topic', tone: 'text-warning-ink' },
  GroupResubscribe: { icon: Check, label: 'Re-subscribed to a topic', tone: 'text-success-ink' },
  AccountStatusChange: { icon: ShieldAlert, label: 'Provider account change', tone: 'text-muted' },
};

function Events({
  events,
  nextBefore,
  onMore,
}: {
  events: RecipientEvent[];
  nextBefore: string | null;
  onMore: () => void;
}) {
  if (events.length === 0) {
    return (
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
          What happened
        </h3>

        <p className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-muted">
          Nothing came back for this person. That is normal when a message was never sent — and it is
          also what you see in the first minute after a send, before the provider reports anything.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">What happened</h3>

      <ol className="flex flex-col">
        {events.map((event, index) => {
          const style = eventStyles[event.type];
          const Icon = style.icon;

          return (
            <li
              key={`${event.occurredAt}-${event.type}-${index}`}
              className="flex gap-3 border-b border-border py-2 last:border-b-0"
            >
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.tone}`} aria-hidden="true" />

              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">
                  {style.label}
                  {/* Not colour alone, and not hidden: an automated open is a real signal about
                      deliverability, just not evidence anybody read it. */}
                  {event.isMachineOpen && (
                    <span className="ml-2 rounded bg-chip px-1.5 py-0.5 text-xs text-muted">
                      automated
                    </span>
                  )}
                  {event.isSuspectedBot && (
                    <span className="ml-2 rounded bg-chip px-1.5 py-0.5 text-xs text-muted">
                      looks like a scanner
                    </span>
                  )}
                </p>

                {event.url !== null && (
                  <p className="mt-0.5 truncate text-xs text-muted">{event.url}</p>
                )}

                {event.reason !== null && (
                  <p className="mt-0.5 text-xs text-muted">{event.reason}</p>
                )}

                <p className="mt-0.5 text-xs text-muted">
                  {formatDateTime(event.occurredAt)}
                  {describeClient(event)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {nextBefore !== null && (
        <Button variant="ghost" size="sm" className="mt-2" onClick={onMore}>
          Show earlier
        </Button>
      )}
    </section>
  );
}

/**
 * Client, device and country. No IP address — the server does not return one, deliberately, and this
 * would be the place it leaked to if it did.
 */
function describeClient(event: RecipientEvent): string {
  const parts = [event.emailClient, event.operatingSystem, event.countryCode].filter(
    (part): part is string => part !== null && part !== '',
  );

  return parts.length === 0 ? '' : ` · ${parts.join(', ')}`;
}
